import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, X, Plus, Check } from "lucide-react";
import SchoolLogo from "@/components/SchoolLogo";
import ImageUploadField from "@/components/ImageUploadField";

const ASSOCIATIONS = ["NCAA", "NAIA", "NJCAA", "CCCAA", "U SPORTS", "CCAA", "Other"];
const DIVISIONS = ["NCAA D1", "NCAA D2", "NCAA D3", "NAIA", "JUCO", "U Sports", "CCAA", "Pro", "Other"];

/**
 * Reusable school picker. Search the directory, select an existing school, or
 * (admins) create a new one inline. Calls onChange(schoolObject | null).
 */
export default function SchoolSelect({ value, onChange, canCreate = true, testid = "school-select" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef();
  const boxRef = useRef();

  const search = useCallback(async (term) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/schools/search`, { params: { q: term } });
      setResults(data.results || []);
    } catch { setResults([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!open) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(q), 250);
    return () => clearTimeout(timer.current);
  }, [q, open, search]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) { setOpen(false); } };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (school) => { onChange(school); setOpen(false); setQ(""); };

  const openCreate = () => {
    setForm({ name: q, city: "", region: "", country: "USA", website: "", association: "", division: "", logo_url: "", logo_dark_url: "" });
    setCreating(true);
  };

  const saveNew = async () => {
    if (!form.name.trim()) { toast.error("School name is required"); return; }
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" ? null : v]));
      const { data } = await api.post(`/admin/schools`, payload);
      toast.success("School added to directory");
      pick(data);
      setCreating(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.existing) {
        toast.message("This school already exists — selected it for you.");
        pick(d.existing);
        setCreating(false);
      } else {
        toast.error(typeof d === "string" ? d : "Could not create school");
      }
    } finally { setBusy(false); }
  };

  // Selected state
  if (value && !creating) {
    return (
      <div data-testid={`${testid}-selected`} className="mt-1 flex items-center gap-3 border border-slate-300 rounded-sm p-2 bg-white">
        <SchoolLogo name={value.name} logoUrl={value.logo_url} darkLogoUrl={value.logo_dark_url} size="md" testid={`${testid}-logo`} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-[#0A192F] truncate">{value.name}</div>
          <div className="text-[11px] text-slate-500 truncate">
            {[value.division, [value.city, value.region].filter(Boolean).join(", ")].filter(Boolean).join(" · ") || "—"}
          </div>
        </div>
        <button type="button" data-testid={`${testid}-clear`} onClick={() => onChange(null)}
          className="text-slate-400 hover:text-[#E63946] shrink-0"><X className="w-4 h-4" /></button>
      </div>
    );
  }

  // Inline create form
  if (creating) {
    return (
      <div data-testid={`${testid}-create`} className="mt-1 border border-slate-300 rounded-sm p-3 bg-slate-50 space-y-2">
        <div className="flex items-center justify-between">
          <span className="label-overline text-[10px]">New School</span>
          <button type="button" onClick={() => setCreating(false)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>
        <input data-testid={`${testid}-name`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Official school name" className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <input data-testid={`${testid}-city`} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" className="px-2 py-1.5 border border-slate-300 rounded-sm text-sm" />
          <input data-testid={`${testid}-region`} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="Province / State" className="px-2 py-1.5 border border-slate-300 rounded-sm text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input data-testid={`${testid}-country`} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Country" className="px-2 py-1.5 border border-slate-300 rounded-sm text-sm" />
          <input data-testid={`${testid}-website`} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="Website" className="px-2 py-1.5 border border-slate-300 rounded-sm text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select data-testid={`${testid}-association`} value={form.association} onChange={(e) => setForm({ ...form, association: e.target.value })} className="px-2 py-1.5 border border-slate-300 rounded-sm text-sm bg-white">
            <option value="">Association</option>{ASSOCIATIONS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select data-testid={`${testid}-division`} value={form.division} onChange={(e) => setForm({ ...form, division: e.target.value })} className="px-2 py-1.5 border border-slate-300 rounded-sm text-sm bg-white">
            <option value="">Division</option>{DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <ImageUploadField label="School Logo (upload once, reused everywhere)" value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} testid={`${testid}-logo-upload`} aspect="aspect-square" />
        <button type="button" data-testid={`${testid}-save-new`} onClick={saveNew} disabled={busy}
          className="w-full bg-[#0A192F] text-white px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60 inline-flex items-center justify-center gap-1">
          <Check className="w-4 h-4" /> {busy ? "Saving…" : "Add & Select"}
        </button>
      </div>
    );
  }

  // Search state
  return (
    <div ref={boxRef} className="relative mt-1" data-testid={testid}>
      <div className="flex items-center border border-slate-300 rounded-sm bg-white">
        <Search className="w-4 h-4 text-slate-400 ml-2" />
        <input data-testid={`${testid}-input`} value={q} onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          placeholder="Search schools…" className="flex-1 px-2 py-2 text-sm outline-none rounded-sm" />
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-72 overflow-auto bg-white border border-slate-200 rounded-sm shadow-lg">
          {loading && <div className="px-3 py-2 text-xs text-slate-400">Searching…</div>}
          {!loading && results.map((s) => (
            <button type="button" key={s.id} data-testid={`${testid}-option-${s.id}`} onClick={() => pick(s)}
              className="w-full flex items-center gap-2 px-2 py-2 hover:bg-slate-50 text-left">
              <SchoolLogo name={s.name} logoUrl={s.logo_url} size="sm" />
              <div className="min-w-0">
                <div className="text-sm text-[#0A192F] truncate">{s.name}</div>
                <div className="text-[11px] text-slate-500 truncate">{[s.division, [s.city, s.region].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}</div>
              </div>
            </button>
          ))}
          {!loading && !results.length && (
            <div className="px-3 py-2 text-xs text-slate-500">{q.trim() ? "No matching schools." : "Type to search the directory."}</div>
          )}
          {canCreate && (
            <button type="button" data-testid={`${testid}-create-btn`} onClick={openCreate}
              className="w-full flex items-center gap-2 px-3 py-2 border-t border-slate-100 text-xs font-semibold text-[#E63946] hover:bg-red-50">
              <Plus className="w-4 h-4" /> Create new school{q.trim() ? ` "${q.trim()}"` : ""}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
